/**
 * Client-seitige Bild-Kompression vor dem Upload.
 *
 * Skaliert auf max 2560 px lange Kante, JPEG Quality 0.85. Damit
 * landen Smartphone-Fotos (typisch 4000×3000 / ~5 MB) bei ~700–1200 KB —
 * Detail-treu genug fuer Baudokumentation, aber im Mobilfunk in
 * akzeptabler Zeit hochladbar.
 *
 * EXIF-Orientation:
 *   Wir laden das Foto via HTMLImageElement statt createImageBitmap.
 *   Alle modernen Browser inkl. iOS Safari 13.4+ respektieren bei
 *   `<img>` automatisch die EXIF-Orientation — naturalWidth/Height
 *   sind die bereits-rotierten Dimensionen, drawImage zeichnet das
 *   Bild korrekt orientiert. createImageBitmap hat hier auf iOS
 *   einen langjaehrigen Bug, der zu Querformat-Resultaten fuehrte.
 *
 * Beim Re-Encode zu JPEG werden EXIF-Daten (inkl. GPS) verworfen.
 */

const MAX_DIMENSION = 2560;
const JPEG_QUALITY = 0.85;

export interface CompressedImage {
  blob: Blob;
  width: number;
  height: number;
  originalName: string;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Bild konnte nicht dekodiert werden'));
    img.src = url;
  });
}

export async function compressImage(file: File): Promise<CompressedImage> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    // decode() blockt bis die Pixel im Speicher sind. Auf manchen
    // Browsern unterstuetzt; bei Fehler ignorieren — onload reicht.
    try {
      await img.decode();
    } catch {
      /* ignore */
    }

    // naturalWidth/Height sind die orientierten Pixel-Dimensionen
    // (Browser hat die EXIF-Rotation schon angewandt).
    const orientedW = img.naturalWidth;
    const orientedH = img.naturalHeight;
    if (!orientedW || !orientedH) {
      throw new Error('Bild ohne gueltige Dimensionen');
    }

    const longest = Math.max(orientedW, orientedH);
    const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
    const targetW = Math.round(orientedW * scale);
    const targetH = Math.round(orientedH * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas-Kontext nicht verfügbar');

    // Hoehere Qualitaet bei der Skalierung — Default ist 'low' auf manchen
    // Browsern.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Kompression fehlgeschlagen'))),
        'image/jpeg',
        JPEG_QUALITY,
      );
    });

    return {
      blob,
      width: targetW,
      height: targetH,
      originalName: file.name,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function isCompressibleImage(file: File): boolean {
  // HEIC vom iPhone schickt der Browser oft als image/heic ODER
  // image/heif. Modernes Safari/iOS kann das im <img>-Element laden,
  // Desktop-Chrome/Firefox NICHT. Wir versuchen es trotzdem — bei
  // Fehler im Caller wird das Original hochgeladen.
  return /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
